/**
 * Advisor Service
 * API calls for managing advisors and their goals
 */

import { apiClient } from '@/lib/api-client';
import type {
  Advisor,
  AdvisorFormData,
  TeamSummary,
  AdvisorStats,
  ToggleAvailabilityData,
} from '@/types/advisor';

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const BASE_URL = '/auth/advisors';

export const advisorService = {
  /**
   * Get all advisors
   * @param params.project_id - Filter by project ID
   * @param params.company_id - Filter by company ID (required for superusers)
   */
  async getAll(params?: { project_id?: string; company_id?: string }): Promise<Advisor[]> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) {
      queryParams.append('project', params.project_id);
    }
    if (params?.company_id) {
      queryParams.append('company', params.company_id);
    }

    const url = queryParams.toString() ? `${BASE_URL}/?${queryParams}` : `${BASE_URL}/`;
    const result = await apiClient.get<Advisor[] | { results: Advisor[]; count: number }>(url);

    // Handle paginated response from DRF
    if (Array.isArray(result)) {
      return result;
    }
    // Paginated response: { count, results, next, previous }
    if (result && 'results' in result && Array.isArray(result.results)) {
      return result.results;
    }
    // Fallback: return empty array if unexpected format
    return [];
  },

  /**
   * Get advisors with pagination (server-side)
   */
  async getAllPaginated(params?: {
    project_id?: string;
    company_id?: string;
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<PaginatedResponse<Advisor>> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project', params.project_id);
    if (params?.company_id) queryParams.append('company', params.company_id);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.page_size) queryParams.append('page_size', String(params.page_size));
    if (params?.search) queryParams.append('search', params.search);

    const url = queryParams.toString() ? `${BASE_URL}/?${queryParams}` : `${BASE_URL}/`;
    const result = await apiClient.get<PaginatedResponse<Advisor> | Advisor[]>(url);

    // Handle non-paginated response
    if (Array.isArray(result)) {
      return { count: result.length, next: null, previous: null, results: result };
    }
    if (result && 'results' in result) {
      return result as PaginatedResponse<Advisor>;
    }
    return { count: 0, next: null, previous: null, results: [] };
  },

  /**
   * Get single advisor
   */
  async getById(id: string): Promise<Advisor> {
    return apiClient.get<Advisor>(`${BASE_URL}/${id}/`);
  },

  /**
   * Get team summary
   */
  async getTeamSummary(params?: { project_id?: string; company_id?: string }): Promise<TeamSummary> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project', params.project_id);
    if (params?.company_id) queryParams.append('company', params.company_id);
    const url = queryParams.toString() ? `${BASE_URL}/team_summary/?${queryParams}` : `${BASE_URL}/team_summary/`;
    return apiClient.get<TeamSummary>(url);
  },

  /**
   * Get advisor stats
   */
  async getStats(id: string): Promise<AdvisorStats> {
    return apiClient.get<AdvisorStats>(`${BASE_URL}/${id}/stats/`);
  },

  /**
   * Update advisor
   */
  async update(id: string, data: AdvisorFormData): Promise<Advisor> {
    return apiClient.patch<Advisor>(`${BASE_URL}/${id}/`, data);
  },

  /**
   * Toggle availability
   */
  async toggleAvailability(id: string, data: ToggleAvailabilityData): Promise<{ message: string; is_available_for_assignment: boolean }> {
    return apiClient.post<{ message: string; is_available_for_assignment: boolean }>(
      `${BASE_URL}/${id}/toggle_availability/`,
      data
    );
  },

  /**
   * Assign goal to advisor
   */
  async assignGoal(advisorId: string, goalId: string | null): Promise<Advisor> {
    return apiClient.patch<Advisor>(`${BASE_URL}/${advisorId}/`, {
      assigned_goal_id: goalId,
    });
  },

  /**
   * Update max active leads
   */
  async updateMaxLeads(advisorId: string, maxLeads: number): Promise<Advisor> {
    return apiClient.patch<Advisor>(`${BASE_URL}/${advisorId}/`, {
      max_active_leads: maxLeads,
    });
  },

  /**
   * Update availability schedule
   */
  async updateSchedule(advisorId: string, schedule: AdvisorFormData['availability_schedule']): Promise<Advisor> {
    return apiClient.patch<Advisor>(`${BASE_URL}/${advisorId}/`, {
      availability_schedule: schedule,
    });
  },
};
